import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { sanitizeBrowserQueryParams } from "@/utils/form/queryParams";

interface ParamMapping {
  [frontendKey: string]: string; // frontendKey -> backendKey
}

interface UseParamMapperProps {
  paramMapping: ParamMapping;
  scope?: string;
}

/**
 * Maps sanitized browser query params to backend query keys.
 *
 * @param {UseParamMapperProps} props - Mapping configuration.
 * @returns {{ mappedQueryString: string }} Backend-ready query string.
 */
export const useParamMapper = ({
  paramMapping,
  scope,
}: UseParamMapperProps): {
  mappedQueryString: string;
} => {
  const [searchParams] = useSearchParams();

  // Create mapped query string for backend
  const mappedQueryString = useMemo(() => {
    const params = new URLSearchParams();
    const browserParams = sanitizeBrowserQueryParams(searchParams);

    Object.entries(paramMapping).forEach(([frontendKey, backendKey]) => {
      // Create scoped key if scope is provided
      const effectiveFrontendKey = scope
        ? `${scope}_${frontendKey}`
        : frontendKey;
      const value = browserParams.get(effectiveFrontendKey);

      if (value) {
        params.set(backendKey, value);
      }
    });

    const queryString = params.toString();

    return queryString ? `&${queryString}` : "";
  }, [searchParams, paramMapping, scope]);

  return {
    mappedQueryString,
  };
};
